<template>
  <div class="legal-page">
    <!-- Navbar -->
    <MainNavbar />

    <!-- Hero Banner -->
    <div class="legal-hero buyer-terms-hero">
      <div class="hero-content">
        <h1>IPA Buyer Terms</h1>
        <p>Marketplace Liability Disclaimer for Customers Purchasing from Third-Party Sellers</p>
        <div class="last-updated">
          <i class="fa-solid fa-calendar"></i>
          Last Updated: April 27, 2026
        </div>
      </div>
      <LegalDocumentSeal />
    </div>

    <!-- Warning Notice -->
    <div class="warning-notice">
      <div class="warning-icon">
        <i class="fa-solid fa-triangle-exclamation"></i>
      </div>
      <div class="warning-content">
        <h3>Important Notice to Buyers</h3>
        <p>IPA operates as a marketplace platform only. We connect buyers with independent sellers but do not own, inspect, or deliver the products listed.</p>
      </div>
    </div>

    <!-- Main Content -->
    <div class="legal-container">
      <div class="legal-content buyer-terms-content">
        <!-- Introduction -->
        <div class="legal-section">
          <h2 class="section-title">
            <i class="fa-solid fa-scale-balanced"></i>
            Marketplace Platform Disclaimer
          </h2>
          <div class="disclaimer-card">
            <div class="disclaimer-icon">
              <i class="fa-solid fa-store"></i>
            </div>
            <div class="disclaimer-text">
              <p>
                IPA operates as a <strong>marketplace platform</strong> that connects Buyers and independent Sellers.
                IPA does <strong>not</strong> own, create, inspect, or deliver the items listed on the platform.
              </p>
              <p>
                By using IPA to purchase from third-party sellers, you agree to the following terms and conditions.
              </p>
            </div>
          </div>
        </div>

        <!-- Terms Sections -->
        <div class="terms-grid">
          <!-- Third-Party Seller Responsibility -->
          <div class="term-card" data-aos="fade-up">
            <div class="term-number">1</div>
            <div class="term-content">
              <h3>
                <i class="fa-solid fa-user-tie"></i>
                Third-Party Seller Responsibility
              </h3>
              <ul class="term-list">
                <li>All products are sold directly by independent Sellers, <strong>not by IPA</strong>.</li>
                <li>The Seller is fully responsible for product quality, accuracy, authenticity, condition, and delivery.</li>
                <li>Buyers must verify and inspect products <strong>before finalizing</strong> exchange or payment.</li>
              </ul>
            </div>
          </div>

          <!-- IPA Is Not a Party to the Transaction -->
          <div class="term-card" data-aos="fade-up" data-aos-delay="100">
            <div class="term-number">2</div>
            <div class="term-content">
              <h3>
                <i class="fa-solid fa-handshake-slash"></i>
                IPA Is Not a Party to the Transaction
              </h3>
              <p class="term-subtitle">IPA is <strong>not involved</strong> in:</p>
              <div class="exclusion-grid">
                <div class="exclusion-item">
                  <i class="fa-solid fa-money-bill-transfer"></i>
                  <span>Payment between buyer and seller</span>
                </div>
                <div class="exclusion-item">
                  <i class="fa-solid fa-truck"></i>
                  <span>Delivery arrangements</span>
                </div>
                <div class="exclusion-item">
                  <i class="fa-solid fa-comments"></i>
                  <span>Negotiations</span>
                </div>
                <div class="exclusion-item">
                  <i class="fa-solid fa-people-arrows"></i>
                  <span>Physical exchange</span>
                </div>
                <div class="exclusion-item">
                  <i class="fa-solid fa-scale-balanced"></i>
                  <span>Disputes over product quality or pricing</span>
                </div>
              </div>
              <p class="term-note">
                IPA only provides a digital listing and communication platform.
              </p>
            </div>
          </div>

          <!-- No Guarantees on Product Quality -->
          <div class="term-card" data-aos="fade-up" data-aos-delay="200">
            <div class="term-number">3</div>
            <div class="term-content">
              <h3>
                <i class="fa-solid fa-shield-xmark"></i>
                No Guarantees on Product Quality
              </h3>
              <p class="term-subtitle">IPA does <strong>not guarantee</strong>:</p>
              <div class="guarantee-list">
                <div class="guarantee-item">
                  <i class="fa-solid fa-xmark"></i>
                  <span>That the item matches the listing</span>
                </div>
                <div class="guarantee-item">
                  <i class="fa-solid fa-xmark"></i>
                  <span>That the product is authentic or defect-free</span>
                </div>
                <div class="guarantee-item">
                  <i class="fa-solid fa-xmark"></i>
                  <span>That images or descriptions are fully accurate</span>
                </div>
                <div class="guarantee-item">
                  <i class="fa-solid fa-xmark"></i>
                  <span>That the Seller will deliver as agreed</span>
                </div>
                <div class="guarantee-item">
                  <i class="fa-solid fa-xmark"></i>
                  <span>The performance, durability, or safety of products</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Meeting & Safety Disclaimer -->
          <div class="term-card" data-aos="fade-up" data-aos-delay="300">
            <div class="term-number">4</div>
            <div class="term-content">
              <h3>
                <i class="fa-solid fa-user-shield"></i>
                Meeting & Safety Disclaimer
              </h3>
              <p class="term-subtitle">Buyers <strong>must</strong>:</p>
              <ul class="term-list">
                <li>Arrange safe, public meeting locations</li>
                <li>Verify the product before payment</li>
                <li>Avoid sending money to unknown parties</li>
                <li>Report suspicious activity to IPA</li>
              </ul>
              <div class="safety-alert">
                <i class="fa-solid fa-circle-exclamation"></i>
                <p>IPA is <strong>not responsible</strong> for incidents during meet-ups.</p>
              </div>
            </div>
          </div>

          <!-- Payments & Refunds -->
          <div class="term-card" data-aos="fade-up" data-aos-delay="400">
            <div class="term-number">5</div>
            <div class="term-content">
              <h3>
                <i class="fa-solid fa-credit-card"></i>
                Payments & Refunds
              </h3>
              <div class="payment-info">
                <p><strong>Unless IPA provides an official payment system:</strong></p>
                <ul class="term-list">
                  <li>Payments are strictly between Buyer and Seller</li>
                  <li>IPA does not handle refunds, chargebacks, or compensation</li>
                  <li>Buyers must verify all payments before confirming orders</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Dispute Resolution -->
          <div class="term-card" data-aos="fade-up" data-aos-delay="500">
            <div class="term-number">6</div>
            <div class="term-content">
              <h3>
                <i class="fa-solid fa-gavel"></i>
                Dispute Resolution
              </h3>
              <div class="dispute-info">
                <p>Any dispute between Buyer and Seller must be resolved <strong>directly between the two parties</strong>.</p>
                <div class="dispute-note">
                  <i class="fa-solid fa-info-circle"></i>
                  <p>IPA may assist with communication but is <strong>not obligated</strong> to mediate or settle disputes.</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Fraud & Misconduct -->
          <div class="term-card" data-aos="fade-up" data-aos-delay="600">
            <div class="term-number">7</div>
            <div class="term-content">
              <h3>
                <i class="fa-solid fa-user-secret"></i>
                Fraud & Misconduct
              </h3>
              <div class="fraud-info">
                <p>If a Buyer reports a fraudulent Seller:</p>
                <ul class="term-list">
                  <li>IPA may investigate</li>
                  <li>IPA may suspend or remove the Seller's account</li>
                  <li>IPA does not compensate the Buyer unless an IPA-managed payment guarantee system was explicitly used</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Limitation of Liability -->
          <div class="term-card" data-aos="fade-up" data-aos-delay="700">
            <div class="term-number">8</div>
            <div class="term-content">
              <h3>
                <i class="fa-solid fa-scale-unbalanced"></i>
                Limitation of Liability
              </h3>
              <p class="term-subtitle">To the fullest extent permitted by law, IPA is <strong>not liable</strong> for:</p>
              <div class="liability-grid">
                <div class="liability-item">Lost money</div>
                <div class="liability-item">Product defects</div>
                <div class="liability-item">Fraudulent sellers</div>
                <div class="liability-item">Delivery failures</div>
                <div class="liability-item">Misrepresentations</div>
                <div class="liability-item">Physical harm</div>
                <div class="liability-item">Property damage</div>
                <div class="liability-item">Consequential damages</div>
              </div>
              <div class="liability-warning">
                <i class="fa-solid fa-triangle-exclamation"></i>
                <p>Buyers use the platform at <strong>their own risk</strong>.</p>
              </div>
            </div>
          </div>

          <!-- Buyer Obligations -->
          <div class="term-card" data-aos="fade-up" data-aos-delay="800">
            <div class="term-number">9</div>
            <div class="term-content">
              <h3>
                <i class="fa-solid fa-clipboard-check"></i>
                Buyer Obligations
              </h3>
              <p class="term-subtitle">Buyers <strong>must</strong>:</p>
              <div class="obligations-grid">
                <div class="obligation-item">
                  <i class="fa-solid fa-user-check"></i>
                  <span>Provide accurate contact information</span>
                </div>
                <div class="obligation-item">
                  <i class="fa-solid fa-magnifying-glass"></i>
                  <span>Inspect items before payment</span>
                </div>
                <div class="obligation-item">
                  <i class="fa-solid fa-scale-balanced"></i>
                  <span>Follow local laws and safety guidelines</span>
                </div>
                <div class="obligation-item">
                  <i class="fa-solid fa-ban"></i>
                  <span>Avoid abusive behavior toward sellers</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Amendments -->
          <div class="term-card" data-aos="fade-up" data-aos-delay="900">
            <div class="term-number">10</div>
            <div class="term-content">
              <h3>
                <i class="fa-solid fa-file-contract"></i>
                Amendments
              </h3>
              <div class="amendments-info">
                <p>IPA may update these terms at any time.</p>
                <div class="amendment-note">
                  <i class="fa-solid fa-circle-info"></i>
                  <p>Continued platform use indicates acceptance of updates.</p>
                </div>
                <div class="version-info">
                  <i class="fa-solid fa-clock-rotate-left"></i>
                  <span>Current Version: 1.0.0 | Effective Date: April 27, 2026</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Important Summary -->
        <div class="summary-section">
          <h3 class="summary-title">
            <i class="fa-solid fa-lightbulb"></i>
            Key Takeaways for Buyers
          </h3>
          <div class="key-takeaways">
            <div class="takeaway">
              <i class="fa-solid fa-store"></i>
              <p><strong>IPA is a platform only</strong> – We connect you with sellers</p>
            </div>
            <div class="takeaway">
              <i class="fa-solid fa-magnifying-glass"></i>
              <p><strong>Inspect before you pay</strong> – Verify products in person</p>
            </div>
            <div class="takeaway">
              <i class="fa-solid fa-shield-halved"></i>
              <p><strong>Use safe meeting spots</strong> – Public locations only</p>
            </div>
            <div class="takeaway">
              <i class="fa-solid fa-money-check-dollar"></i>
              <p><strong>Use IPA payment when available</strong> – For buyer protection</p>
            </div>
          </div>
        </div>

        <!-- Footer Notice -->
        <div class="legal-footer-notice buyer-terms-footer">
          <div class="footer-content">
            <div class="footer-left">
              <h4>Need Help?</h4>
              <p>
                <i class="fa-solid fa-circle-question"></i>
                Contact IPA Support for platform-related issues
              </p>
              <p>
                <i class="fa-solid fa-scale-balanced"></i>
                For transaction disputes, contact the seller directly
              </p>
            </div>
            <div class="footer-right">
              <h4>Document Information</h4>
              <p><i class="fa-solid fa-file-lines"></i> IPA Buyer Terms & Conditions</p>
              <p><i class="fa-solid fa-calendar"></i> Version 1.0.0 (April 27, 2026)</p>
              <p><i class="fa-solid fa-location-dot"></i> IPA Legal Department, Nairobi</p>
            </div>
          </div>
          <div class="copyright">
            <p>© 2026 IPA Marketplace. All rights reserved.</p>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="legal-actions buyer-terms-actions">
          <router-link :to="{ name: 'BuyerProductView' }" class="back-btn">
            <i class="fa-solid fa-arrow-left"></i>
            Back to Marketplace
          </router-link>
          <router-link to="/ipromiseafrica/legal/seller-terms" class="secondary-btn">
            <i class="fa-solid fa-file-contract"></i>
            View Seller Terms
          </router-link>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <MainFooter />
  </div>
</template>

<script>
import MainNavbar from "../../components/main/headernfooter/header.vue";
import MainFooter from "../../components/main/headernfooter/Footer.vue";
import LegalDocumentSeal from "../../components/legal/LegalDocumentSeal.vue";
import AOS from 'aos';
import 'aos/dist/aos.css';

export default {
  name: "BuyerTerms",
  components: {
    MainNavbar,
    MainFooter,
    LegalDocumentSeal,
  },
  mounted() {
    // Initialize animations
    AOS.init({
      duration: 600,
      once: true,
      offset: 100
    });
    
    // Scroll to top
    window.scrollTo(0, 0);
  }
};
</script>

<style scoped>
.legal-page {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  font-family: "Poppins", sans-serif;
  background: var(--background-color1);
  color: var(--text-color1);
  overflow-x: clip;
  -webkit-font-smoothing: antialiased;
}

.legal-hero.buyer-terms-hero {
  background: linear-gradient(
    145deg,
    var(--background-color2) 0%,
    color-mix(in srgb, var(--background-color1) 88%, var(--background-color2)) 100%
  );
  color: var(--text-color1);
  padding: clamp(3rem, 8vw, 5rem) clamp(1rem, 4vw, 1.5rem) clamp(2.5rem, 6vw, 3.75rem);
  text-align: center;
  margin-top: max(70px, calc(env(safe-area-inset-top, 0px) + 64px));
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid color-mix(in srgb, var(--border-color-hover) 22%, transparent);
}

.legal-hero.buyer-terms-hero .hero-content {
  position: relative;
  z-index: 2;
}

.legal-hero.buyer-terms-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 20% 80%, color-mix(in srgb, var(--border-color-hover) 12%, transparent) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, color-mix(in srgb, var(--border-color-hover) 6%, transparent) 0%, transparent 50%);
  pointer-events: none;
}

.hero-content h1 {
  font-size: clamp(1.85rem, 4.5vw, 3rem);
  font-weight: 800;
  margin-bottom: 15px;
  color: var(--text-color1);
  letter-spacing: 0.02em;
}

.hero-content > p {
  font-size: clamp(0.95rem, 2.2vw, 1.2rem);
  color: var(--text-color2);
  max-width: 800px;
  margin: 0 auto 20px;
  line-height: 1.6;
}

.last-updated {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: color-mix(in srgb, var(--border-color-hover) 12%, transparent);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  color: var(--border-color-hover);
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 30%, transparent);
}

.warning-notice {
  background: color-mix(in srgb, #ff9800 12%, var(--background-color2));
  border: 1px solid color-mix(in srgb, #ff9800 40%, transparent);
  border-radius: 12px;
  margin: clamp(1.25rem, 3vw, 1.75rem) auto;
  max-width: min(1200px, 100% - 2rem);
  padding: clamp(1rem, 3vw, 1.25rem);
  display: flex;
  align-items: center;
  gap: 20px;
}

.warning-icon {
  color: #ffb74d;
  font-size: 2rem;
  flex-shrink: 0;
}

.warning-content h3 {
  color: var(--text-color1);
  margin: 0 0 8px 0;
  font-size: 1.1rem;
}

.warning-content p {
  color: var(--text-color2);
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.5;
}

.legal-container {
  flex: 1 0 auto;
  width: 100%;
  max-width: min(1200px, 100% - 2rem);
  margin: clamp(1.5rem, 4vw, 2.5rem) auto;
  padding: 0 clamp(0.85rem, 3vw, 1.25rem);
  box-sizing: border-box;
}

.buyer-terms-content {
  background: var(--background-color1);
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 20%, transparent);
  border-radius: clamp(10px, 2vw, 14px);
  box-shadow: 0 8px 28px color-mix(in srgb, var(--border-color-hover) 6%, transparent);
  padding: clamp(1.25rem, 4vw, 2.5rem);
  position: relative;
  overflow: hidden;
}

.legal-section {
  margin-bottom: clamp(1.25rem, 3vw, 1.75rem);
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  color: var(--text-color1);
  font-size: clamp(1.35rem, 3vw, 1.8rem);
  font-weight: 700;
  margin-bottom: 1.25rem;
  padding-bottom: 0.65rem;
  border-bottom: 2px solid var(--border-color-hover);
}

.section-title i {
  color: var(--border-color-hover);
}

.disclaimer-card {
  background: color-mix(in srgb, var(--border-color-hover) 10%, var(--background-color2));
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 28%, transparent);
  border-radius: 12px;
  padding: clamp(1rem, 3vw, 1.5rem);
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: clamp(1.5rem, 4vw, 2.5rem);
}

.disclaimer-icon {
  background: var(--border-color-hover);
  color: var(--text-color3);
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.disclaimer-text p {
  margin: 0 0 10px 0;
  color: var(--text-color2);
  font-size: 1rem;
  line-height: 1.6;
}

.disclaimer-text p strong {
  color: var(--text-color1);
}

.disclaimer-text p:last-child {
  margin-bottom: 0;
}

.terms-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 500px), 1fr));
  gap: clamp(1rem, 2.5vw, 1.5rem);
  margin-bottom: clamp(2rem, 5vw, 3rem);
}

.term-card {
  background: var(--background-color2);
  border-radius: 12px;
  padding: clamp(1rem, 3vw, 1.5rem);
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 14%, transparent);
  transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
  position: relative;
  overflow: hidden;
}

.term-card:hover {
  border-color: color-mix(in srgb, var(--border-color-hover) 40%, transparent);
  box-shadow: 0 8px 22px color-mix(in srgb, var(--border-color-hover) 10%, transparent);
  transform: translateY(-2px);
}

.term-number {
  position: absolute;
  top: 10px;
  right: 10px;
  background: color-mix(in srgb, var(--border-color-hover) 15%, var(--background-color1));
  color: var(--border-color-hover);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
}

.term-content h3 {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-color1);
  font-size: clamp(1rem, 2vw, 1.2rem);
  margin: 0 0 15px 0;
}

.term-content h3 i {
  color: var(--border-color-hover);
}

.term-content p {
  color: var(--text-color2);
  line-height: 1.6;
}

.term-subtitle {
  color: var(--text-color2);
  font-weight: 600;
  margin: 15px 0;
  font-size: 0.95rem;
}

.term-subtitle strong {
  color: var(--text-color1);
}

.term-list {
  padding-left: 1.25rem;
  margin: 15px 0;
}

.term-list li {
  margin-bottom: 8px;
  color: var(--text-color2);
  line-height: 1.5;
}

.term-list li strong {
  color: var(--text-color1);
}

.exclusion-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr));
  gap: 12px;
  margin: 15px 0;
}

.exclusion-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  background: color-mix(in srgb, #f44336 8%, var(--background-color1));
  border-radius: 8px;
  color: var(--text-color2);
  font-size: 0.9rem;
  border: 1px solid color-mix(in srgb, #f44336 25%, transparent);
}

.exclusion-item i {
  color: #ef5350;
}

.guarantee-list {
  margin: 15px 0;
}

.guarantee-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid color-mix(in srgb, var(--text-color2) 10%, transparent);
  color: var(--text-color2);
}

.guarantee-item:last-child {
  border-bottom: none;
}

.guarantee-item i {
  color: #ef5350;
}

.safety-alert {
  background: color-mix(in srgb, #f44336 10%, var(--background-color1));
  border: 1px solid color-mix(in srgb, #f44336 30%, transparent);
  border-radius: 8px;
  padding: 12px;
  margin-top: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.safety-alert i {
  color: #ef5350;
}

.safety-alert p {
  margin: 0;
  color: var(--text-color2);
  font-size: 0.9rem;
}

.payment-warning {
  background: color-mix(in srgb, #ff9800 10%, var(--background-color1));
  border: 1px solid color-mix(in srgb, #ff9800 30%, transparent);
  border-radius: 8px;
  padding: 12px;
  margin-top: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.payment-warning i {
  color: #ffb74d;
}

.payment-warning span {
  color: var(--text-color2);
  font-size: 0.9rem;
}

.dispute-note,
.amendment-note {
  background: color-mix(in srgb, var(--border-color-hover) 10%, var(--background-color1));
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 25%, transparent);
  border-radius: 8px;
  padding: 12px;
  margin-top: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.dispute-note i,
.amendment-note i {
  color: var(--border-color-hover);
}

.dispute-note p,
.amendment-note p {
  margin: 0;
  color: var(--text-color2);
  font-size: 0.9rem;
}

.liability-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 150px), 1fr));
  gap: 10px;
  margin: 15px 0;
}

.liability-item {
  background: color-mix(in srgb, #f44336 10%, var(--background-color1));
  color: var(--text-color2);
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.85rem;
  text-align: center;
  border: 1px solid color-mix(in srgb, #f44336 28%, transparent);
}

.liability-warning {
  background: color-mix(in srgb, #ff9800 10%, var(--background-color1));
  border: 1px solid color-mix(in srgb, #ff9800 30%, transparent);
  border-radius: 8px;
  padding: 12px;
  margin-top: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.liability-warning i {
  color: #ffb74d;
}

.liability-warning p {
  margin: 0;
  color: var(--text-color2);
  font-weight: 600;
}

.obligations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr));
  gap: 12px;
  margin: 15px 0;
}

.obligation-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: color-mix(in srgb, #4caf50 10%, var(--background-color1));
  border-radius: 8px;
  border: 1px solid color-mix(in srgb, #4caf50 28%, transparent);
  color: var(--text-color2);
}

.obligation-item i {
  color: #66bb6a;
}

.version-info {
  background: var(--background-color2);
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 14%, transparent);
  border-radius: 8px;
  padding: 10px;
  margin-top: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: var(--text-color2);
}

.version-info i {
  color: var(--border-color-hover);
}

.summary-section {
  background: color-mix(in srgb, var(--border-color-hover) 8%, var(--background-color2));
  border-radius: 16px;
  padding: clamp(1.25rem, 3vw, 1.75rem);
  margin: clamp(1.5rem, 4vw, 2.5rem) 0;
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 28%, transparent);
}

.summary-title {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-color1);
  font-size: clamp(1.1rem, 2.5vw, 1.3rem);
  margin: 0 0 25px 0;
}

.summary-title i {
  color: var(--border-color-hover);
}

.key-takeaways {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 250px), 1fr));
  gap: 20px;
}

.takeaway {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  background: var(--background-color1);
  border-radius: 10px;
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 18%, transparent);
}

.takeaway i {
  color: var(--border-color-hover);
  font-size: 1.2rem;
  flex-shrink: 0;
}

.takeaway p {
  margin: 0;
  color: var(--text-color2);
  line-height: 1.4;
}

.takeaway p strong {
  color: var(--text-color1);
}

.buyer-terms-footer {
  border-top: 1px solid color-mix(in srgb, var(--text-color2) 12%, transparent);
  padding-top: clamp(1.25rem, 3vw, 1.75rem);
  margin-top: clamp(1.5rem, 4vw, 2.5rem);
}

.footer-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr));
  gap: clamp(1.5rem, 4vw, 2.5rem);
  margin-bottom: 25px;
}

.footer-left h4,
.footer-right h4 {
  color: var(--text-color1);
  margin-bottom: 15px;
  font-size: 1.1rem;
}

.footer-left p,
.footer-right p {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-color2);
  margin: 8px 0;
  font-size: 0.9rem;
}

.footer-left i,
.footer-right i {
  color: var(--border-color-hover);
}

.copyright {
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid color-mix(in srgb, var(--text-color2) 10%, transparent);
  color: var(--text-color2);
  font-size: 0.9rem;
}

.buyer-terms-actions {
  display: flex;
  flex-wrap: wrap;
  gap: clamp(0.75rem, 2vw, 1rem);
  margin-top: clamp(1.5rem, 4vw, 2.5rem);
  padding-top: clamp(1.25rem, 3vw, 1.75rem);
  border-top: 1px solid color-mix(in srgb, var(--text-color2) 12%, transparent);
}

.secondary-btn {
  background: var(--border-color-hover);
  color: var(--text-color3);
  padding: clamp(0.65rem, 2vw, 0.85rem) clamp(1.25rem, 3vw, 1.5rem);
  border-radius: 32px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  min-width: 180px;
  justify-content: center;
  font-weight: 600;
  font-size: clamp(0.88rem, 1.8vw, 0.95rem);
  border: 1px solid var(--border-color-hover);
  min-height: 44px;
}

.secondary-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px color-mix(in srgb, var(--border-color-hover) 28%, transparent);
}

.back-btn {
  background: var(--background-color2);
  color: var(--text-color1);
  padding: clamp(0.65rem, 2vw, 0.85rem) clamp(1.25rem, 3vw, 1.5rem);
  border-radius: 32px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;
  min-width: 180px;
  justify-content: center;
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 25%, transparent);
  font-weight: 600;
  font-size: clamp(0.88rem, 1.8vw, 0.95rem);
  min-height: 44px;
}

.back-btn:hover {
  background: color-mix(in srgb, var(--border-color-hover) 10%, var(--background-color2));
  border-color: var(--border-color-hover);
}

@media (max-width: 768px) {
  .buyer-terms-content {
    padding: clamp(1rem, 3.5vw, 1.5rem);
  }

  .terms-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .term-card {
    padding: 1.25rem;
  }

  .buyer-terms-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .secondary-btn,
  .back-btn {
    width: 100%;
    min-width: auto;
  }

  .warning-notice {
    flex-direction: column;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .disclaimer-card {
    flex-direction: column;
    text-align: center;
    padding: 1.25rem;
  }

  .exclusion-grid,
  .liability-grid,
  .obligations-grid {
    grid-template-columns: 1fr;
  }
}

@media (hover: none) and (pointer: coarse) {
  .term-card:hover {
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .secondary-btn:hover,
  .back-btn:hover,
  .term-card:hover {
    transform: none;
  }
}
</style>